import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { bubbleImageSrc, cargoMediaSrc } from '@/lib/bubbleImage';
import { Users, NotePencil, Microphone, Stop, X, Sparkle } from '@phosphor-icons/react';

const fmtAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); const m = (Date.now() - d.getTime()) / 60000;
  if (m < 1) return 'just now';
  if (m < 60) return `${Math.floor(m)}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
};

const SwarmDetail = () => {
  const { id } = useParams();
  const [swarm, setSwarm] = useState(null);
  const [bubbles, setBubbles] = useState([]);
  const [joinBubble, setJoinBubble] = useState('');
  const [contribKind, setContribKind] = useState('wish');
  const [contribText, setContribText] = useState('');
  const [audioB64, setAudioB64] = useState(null);
  const [recording, setRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sponsoring, setSponsoring] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const sponsor = async () => {
    if (!swarm) return;
    setSponsoring(true);
    try {
      const { data } = await api.post(`/swarms/${id}/sponsor/checkout`,
        { swarm_id: id, origin_url: window.location.href });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not start checkout');
      setSponsoring(false);
    }
  };

  const load = async () => {
    const { data } = await api.get(`/swarms/${id}`);
    setSwarm(data.swarm);
  };

  useEffect(() => {
    load();
    api.get('/bubbles/mine').then(({ data }) => {
      setBubbles(data.bubbles || []);
      if (data.bubbles?.[0]) setJoinBubble(data.bubbles[0].id);
    });
  }, [id]);

  const join = async () => {
    if (!joinBubble) return;
    setSubmitting(true);
    try {
      await api.post(`/swarms/${id}/join`, { bubble_id: joinBubble });
      toast.success('joined.');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'could not join'); }
    finally { setSubmitting(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const b64 = await new Promise((res) => {
          const r = new FileReader(); r.onload = () => res(String(r.result || '').split(',')[1] || ''); r.readAsDataURL(blob);
        });
        setAudioB64(b64);
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start(); recRef.current = rec; setRecording(true);
      setTimeout(() => { if (rec.state === 'recording') rec.stop(); setRecording(false); }, 30000);
    } catch { toast.error('mic denied'); }
  };
  const stopRec = () => { if (recRef.current?.state === 'recording') recRef.current.stop(); setRecording(false); };

  const contribute = async () => {
    if (!joinBubble) { toast.error('pick a bubble first'); return; }
    setSubmitting(true);
    try {
      const body = { bubble_id: joinBubble, kind: contribKind };
      if (contribKind === 'wish') body.text = contribText;
      if (contribKind === 'voice') body.blob_b64 = audioB64;
      await api.post(`/swarms/${id}/contribute`, body);
      toast.success('contributed.');
      setContribText(''); setAudioB64(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'could not'); }
    finally { setSubmitting(false); }
  };

  if (!swarm) return <div className="min-h-screen grid place-items-center text-white/60">listening…</div>;

  const sealed = swarm.sealed;
  const memberIds = new Set((swarm.members || []).map((m) => m.bubble_id));
  const myInside = bubbles.some((b) => memberIds.has(b.id));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-24">
      <Link to="/swarms" className="text-xs text-white/60 hover:text-white">← all swarms</Link>
      <div className="mt-3 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-bx-cyan">{swarm.kind}</div>
        <h1 className="font-display text-4xl font-black mt-1">{swarm.title}</h1>
        <div className="text-sm text-white/65 mt-1">
          hosted by @{swarm.host_username} · {fmtAgo(swarm.created_at)}
          {sealed && <span className="ml-2 text-bx-volt">· sealed</span>}
          {swarm.sponsored && <span className="ml-2 text-bx-gold">· sponsored</span>}
        </div>
        {!sealed && !swarm.sponsored && swarm.host_user_id && (
          <button
            data-testid="swarm-sponsor-btn"
            onClick={sponsor} disabled={sponsoring}
            className="mt-3 px-4 py-2 rounded-full bg-bx-gold/20 hover:bg-bx-gold/30 text-bx-gold text-xs font-bold uppercase tracking-widest">
            {sponsoring ? 'opening checkout…' : 'Sponsor this swarm · $14.99 (cap →500)'}
          </button>
        )}
      </div>

      {sealed && swarm.cover_image_url && (
        <img alt="swarm cover"
          src={`${process.env.REACT_APP_BACKEND_URL}${swarm.cover_image_url}`}
          className="w-full rounded-3xl mb-6" />
      )}

      <div className="glass rounded-2xl p-5 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-white/60 mb-3 flex items-center gap-2">
          <Users size={12} weight="duotone" /> {(swarm.members || []).length} bubble{(swarm.members||[]).length===1?'':'s'} gathered
        </div>
        <div className="flex flex-wrap gap-3">
          {(swarm.members || []).map((m) => {
            const src = bubbleImageSrc(m);
            return (
              <Link key={m.bubble_id} to={`/bubble/${m.bubble_id}`}
                className="flex flex-col items-center gap-1">
                <div className="h-14 w-14 rounded-full overflow-hidden bg-white/5 ring-1 ring-white/20">
                  {src ? <img src={src} alt={m.name} className="h-full w-full object-cover breathe" /> : null}
                </div>
                <span className="text-[10px] text-white/60">{m.name}</span>
              </Link>
            );
          })}
        </div>

        {!sealed && !myInside && (
          <div className="mt-4 flex items-center gap-2">
            <select value={joinBubble}
              data-testid="swarm-join-bubble"
              onChange={(e) => setJoinBubble(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-full px-3 py-2 text-xs focus:outline-none">
              {bubbles.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
            </select>
            <button
              data-testid="swarm-join-btn"
              onClick={join} disabled={submitting || !joinBubble}
              className="btn-volt px-4 py-2 rounded-full text-xs font-bold">
              Bring your Bubble in
            </button>
          </div>
        )}
      </div>

      {!sealed && myInside && (
        <div className="glass rounded-2xl p-5 mb-6">
          <div className="text-[10px] uppercase tracking-widest text-white/60 mb-3">drop something into the swarm</div>
          <div className="flex gap-2 mb-3">
            {[
              { id: 'wish', icon: NotePencil, label: 'Wish' },
              { id: 'voice', icon: Microphone, label: 'Voice' },
            ].map((opt) => (
              <button key={opt.id}
                data-testid={`swarm-contrib-kind-${opt.id}`}
                onClick={() => setContribKind(opt.id)}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest border transition ${
                  contribKind === opt.id ? 'border-bx-cyan bg-bx-cyan/10 text-bx-cyan' : 'border-white/10 text-white/60'
                }`}>
                <opt.icon size={12} weight="duotone" className="inline mr-1" />{opt.label}
              </button>
            ))}
          </div>
          {contribKind === 'wish' && (
            <textarea
              data-testid="swarm-contrib-text"
              value={contribText}
              onChange={(e) => setContribText(e.target.value.slice(0, 160))}
              placeholder="say it short. say it true."
              rows={2}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-bx-cyan mb-3" />
          )}
          {contribKind === 'voice' && (
            <div className="rounded-xl border border-white/10 p-3 flex items-center gap-3 mb-3">
              {!audioB64 && !recording && (
                <button data-testid="swarm-rec-start" onClick={startRec}
                  className="h-10 w-10 rounded-full bg-bx-magenta/20 grid place-items-center">
                  <Microphone size={16} weight="fill" className="text-bx-magenta" />
                </button>
              )}
              {recording && (
                <button data-testid="swarm-rec-stop" onClick={stopRec}
                  className="h-10 w-10 rounded-full bg-red-500/30 grid place-items-center animate-pulse">
                  <Stop size={14} weight="fill" className="text-red-300" />
                </button>
              )}
              {audioB64 && <audio controls src={`data:audio/webm;base64,${audioB64}`} className="flex-1 h-8" />}
              <span className="text-[10px] uppercase tracking-widest text-white/60">
                {recording ? 'recording' : audioB64 ? 'ready' : 'tap to record'}
              </span>
            </div>
          )}
          <button
            data-testid="swarm-contribute-btn"
            onClick={contribute} disabled={submitting || (contribKind === 'wish' && !contribText.trim()) || (contribKind === 'voice' && !audioB64)}
            className="btn-volt px-4 py-2 rounded-full text-xs font-bold flex items-center gap-2 disabled:opacity-30">
            <Sparkle size={12} weight="fill" /> Drop it in
          </button>
        </div>
      )}

      {(swarm.contributions || []).length > 0 && (
        <div className="space-y-3">
          {(swarm.contributions || []).slice().reverse().map((c) => (
            <div key={c.id} data-testid={`swarm-contrib-${c.id}`} className="glass rounded-2xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-bx-cyan mb-1">{c.bubble_name}</div>
              {c.kind === 'wish' && c.text && <div className="italic text-sm">"{c.text}"</div>}
              {c.kind === 'voice' && cargoMediaSrc(c) && (
                <audio controls src={cargoMediaSrc(c)} className="w-full mt-1" />
              )}
              <div className="text-[10px] text-white/55 mt-2">{fmtAgo(c.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SwarmDetail;
